import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Task } from '../../../shared/types.ts';

// Stage chips (To Do / In Progress / In Review / Completed / Overdue) with Hide/Show toggles for the
// Overdue and Completed sections. Shared by the Dashboard, the board workspace and My Tasks.

export type StageKey = 'all' | 'todo' | 'in_progress' | 'in_review' | 'completed' | 'focus';

const isPastDue = (dueDate?: string | null) => {
  if (!dueDate) return false;
  return String(dueDate).slice(0, 10) < new Date().toISOString().split('T')[0];
};

export const isTaskOverdue = (t: Task) =>
  ((t.status as string) === 'overdue' || isPastDue(t.dueDate)) &&
  t.status !== 'done' &&
  (t.status as string) !== 'completed' &&
  t.status !== 'in_review';

export const isTaskCompleted = (t: Task) => t.status === 'done' || (t.status as string) === 'completed';

export const computeStageCounts = (tasks: Task[]) => ({
  todo: tasks.filter((t) => (t.status === 'todo' || t.status === 'backlog') && !isTaskOverdue(t)).length,
  inProgress: tasks.filter((t) => t.status === 'in_progress' && !isTaskOverdue(t)).length,
  inReview: tasks.filter((t) => t.status === 'in_review').length,
  completed: tasks.filter(isTaskCompleted).length,
  overdue: tasks.filter(isTaskOverdue).length,
});

/** Applies the selected stage plus the hidden Overdue / Completed sections */
export const filterByStage = (
  tasks: Task[],
  stage: StageKey,
  _hideOverdue: boolean,
  hideCompleted: boolean
): Task[] =>
  tasks.filter((t) => {
    // Hiding Overdue only removes the Overdue chip/lane; overdue tickets stay in their status columns
    if (hideCompleted && stage !== 'completed' && isTaskCompleted(t)) return false;
    if (stage === 'todo') return (t.status === 'todo' || t.status === 'backlog') && !isTaskOverdue(t);
    if (stage === 'in_progress') return t.status === 'in_progress' && !isTaskOverdue(t);
    if (stage === 'in_review') return t.status === 'in_review';
    if (stage === 'completed') return isTaskCompleted(t);
    if (stage === 'focus') return isTaskOverdue(t);
    return true;
  });

/** A true/false setting remembered per browser as a convenience */
export function usePersistedFlag(key: string): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setValue((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // storage unavailable (e.g. private window): the toggle still works for this session
      }
      return next;
    });
  return [value, toggle];
}

/** Stage selection + the two hide toggles, ready for a screen to use */
export function useStageFilter() {
  const [stage, setStageState] = useState<StageKey>('all');
  const [hideOverdue, toggleOverdueFlag] = usePersistedFlag('pulse_hide_overdue');
  const [hideCompleted, toggleCompletedFlag] = usePersistedFlag('pulse_hide_completed');

  return {
    stage,
    // Clicking the active chip clears it
    selectStage: (next: StageKey) => setStageState((prev) => (prev === next ? 'all' : next)),
    hideOverdue,
    hideCompleted,
    // The stage chip disappears with its section, so drop it from the selection when hiding
    toggleHideOverdue: () => {
      if (!hideOverdue && stage === 'focus') setStageState('all');
      toggleOverdueFlag();
    },
    toggleHideCompleted: () => {
      if (!hideCompleted && stage === 'completed') setStageState('all');
      toggleCompletedFlag();
    },
  };
}

interface StageFilterBarProps {
  tasks: Task[];
  stage: StageKey;
  onSelectStage: (stage: StageKey) => void;
  hideOverdue: boolean;
  hideCompleted: boolean;
  onToggleHideOverdue: () => void;
  onToggleHideCompleted: () => void;
}

const CHIP_IDLE =
  'bg-white dark:bg-[#181b20] border-[#d5d8de]/70 dark:border-surface-800 text-[#121417] dark:text-white shadow-2xs';

export const StageFilterBar: React.FC<StageFilterBarProps> = ({
  tasks,
  stage,
  onSelectStage,
  hideOverdue,
  hideCompleted,
  onToggleHideOverdue,
  onToggleHideCompleted,
}) => {
  const counts = computeStageCounts(tasks);

  const toggleButton = (hidden: boolean, label: string, onClick: () => void, hiddenTone: string) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={hidden}
      title={hidden ? `The ${label} section is hidden — click to show it again` : `Hide the ${label} section and its tickets`}
      className={`px-2.5 py-1 rounded-xl border transition-all flex items-center space-x-1.5 shrink-0 text-[11px] font-bold ${
        hidden ? hiddenTone : `${CHIP_IDLE} hover:bg-surface-50 dark:hover:bg-surface-800`
      }`}
    >
      {hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      <span>{hidden ? 'Show' : 'Hide'} {label}</span>
    </button>
  );

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto touch-scroll no-scrollbar py-0.5 w-full sm:flex-1 sm:min-w-0">
      <button
        type="button"
        onClick={() => onSelectStage('todo')}
        title="Filter tasks in 'To Do' stage"
        className={`group px-2.5 py-1 rounded-xl border transition-all text-left flex items-center space-x-1.5 shrink-0 ${
          stage === 'todo' ? 'bg-amber-500 text-white border-transparent shadow-sm' : `${CHIP_IDLE} hover:bg-amber-50/50 dark:hover:bg-surface-800`
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
        <span className="text-xs font-black leading-none">{counts.todo}</span>
        <span className="text-[11px] font-bold opacity-90">To Do</span>
      </button>

      <button
        type="button"
        onClick={() => onSelectStage('in_progress')}
        title="Filter tasks in 'In Progress' stage"
        className={`group px-2.5 py-1 rounded-xl border transition-all text-left flex items-center space-x-1.5 shrink-0 ${
          stage === 'in_progress' ? 'bg-brand-600 text-white border-transparent shadow-sm' : `${CHIP_IDLE} hover:bg-blue-50/50 dark:hover:bg-surface-800`
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse shrink-0" />
        <span className="text-xs font-black leading-none">{counts.inProgress}</span>
        <span className="text-[11px] font-bold opacity-90">In Progress</span>
      </button>

      <button
        type="button"
        onClick={() => onSelectStage('in_review')}
        title="Filter tasks in 'In Review' stage"
        className={`group px-2.5 py-1 rounded-xl border transition-all text-left flex items-center space-x-1.5 shrink-0 ${
          stage === 'in_review' ? 'bg-purple-600 text-white border-transparent shadow-sm' : `${CHIP_IDLE} hover:bg-purple-50/50 dark:hover:bg-surface-800`
        }`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
        <span className="text-xs font-black leading-none">{counts.inReview}</span>
        <span className="text-[11px] font-bold opacity-90">In Review</span>
      </button>

      {/* Completed (the whole section is removed while hidden) */}
      {!hideCompleted && (
        <button
          type="button"
          onClick={() => onSelectStage('completed')}
          title="Filter completed tasks"
          className={`group px-2.5 py-1 rounded-xl border transition-all text-left flex items-center space-x-1.5 shrink-0 ${
            stage === 'completed' ? 'bg-emerald-600 text-white border-transparent shadow-sm' : `${CHIP_IDLE} hover:bg-emerald-50/50 dark:hover:bg-surface-800`
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
          <span className="text-xs font-black leading-none">{counts.completed}</span>
          <span className="text-[11px] font-bold opacity-90">Completed</span>
        </button>
      )}

      {/* Overdue (the whole section is removed while hidden) */}
      {!hideOverdue && (
        <button
          type="button"
          onClick={() => onSelectStage('focus')}
          title="Filter overdue tasks needing attention"
          className={`group px-2.5 py-1 rounded-xl border transition-all text-left flex items-center space-x-1.5 shrink-0 ${
            stage === 'focus' ? 'bg-rose-600 text-white border-transparent shadow-sm' : `${CHIP_IDLE} hover:bg-rose-50/50 dark:hover:bg-surface-800`
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
          <span
            className={`text-xs font-black leading-none ${
              stage === 'focus' ? 'text-white' : counts.overdue > 0 ? 'text-rose-600 dark:text-rose-400' : ''
            }`}
          >
            {counts.overdue}
          </span>
          <span className="text-[11px] font-bold opacity-90">Overdue</span>
        </button>
      )}

      {stage !== 'all' && (
        <button
          type="button"
          onClick={() => onSelectStage(stage)}
          className="px-2 py-1 text-[11px] font-bold text-surface-500 hover:text-surface-900 dark:hover:text-white hover:underline shrink-0"
        >
          Show All ({tasks.length})
        </button>
      )}

      {/* Hide / Show toggles sit on the right-hand side */}
      <div className="ml-auto pl-2 flex items-center gap-1.5 shrink-0">
      {toggleButton(
        hideCompleted,
        'Completed',
        onToggleHideCompleted,
        'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
      )}
      {toggleButton(
        hideOverdue,
        'Overdue',
        onToggleHideOverdue,
        'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300'
      )}
      </div>
    </div>
  );
};
